import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/common/Button';
import './ConfirmDialog.scss';

export interface ConfirmOptions {
  /** The question, phrased so the answer to "yes" is obvious. */
  title: string;
  /** What actually happens, and whether it can be undone. */
  body?: React.ReactNode;
  /** Name the action rather than saying "OK": "Delete source", "Remove". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger = red confirm button. The default, since these guard destruction. */
  tone?: 'danger' | 'primary';
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

/**
 * Asks the question and resolves to the answer.
 *
 * Promise-based so a guarded action stays one linear function — the shape
 * `if (!(await confirm({…}))) return;` reads like the `window.confirm` calls it
 * replaces, rather than splitting each caller into a handler and a callback.
 */
export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside <ConfirmProvider>.');
  return confirm;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  // Held in a ref rather than in state beside the options: resolving is a side
  // effect, and a state updater can be called twice under StrictMode.
  const resolver = useRef<((answer: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>(
    (options) =>
      new Promise<boolean>((resolve) => {
        // A second question while one is open answers the first "no" rather
        // than leaving its caller waiting on a promise nothing will settle.
        resolver.current?.(false);
        resolver.current = resolve;
        setPending(options);
      }),
    [],
  );

  const answer = useCallback((value: boolean) => {
    const resolve = resolver.current;
    resolver.current = null;
    setPending(null);
    resolve?.(value);
  }, []);

  // Unmounting with a question open must not strand the caller mid-await.
  useEffect(() => () => resolver.current?.(false), []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && <Dialog options={pending} onAnswer={answer} />}
    </ConfirmContext.Provider>
  );
};

const Dialog: React.FC<{ options: ConfirmOptions; onAnswer: (value: boolean) => void }> = ({
  options,
  onAnswer,
}) => {
  const { title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', tone = 'danger' } = options;
  const panel = useRef<HTMLDivElement>(null);
  const cancelButton = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    // Cancel takes focus, not confirm: this guards destructive work, so a
    // reflexive Enter should do nothing rather than go through with it.
    cancelButton.current?.querySelector('button')?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onAnswer(false);
        return;
      }
      if (event.key !== 'Tab') return;

      // Keep focus inside the dialog; there are only ever two buttons.
      const focusable = panel.current?.querySelectorAll('button');
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus?.();
    };
  }, [onAnswer]);

  return (
    <div
      className="confirm"
      // A click that starts inside the panel and ends on the backdrop is a
      // drag, not a dismissal, so only a click landing on the backdrop counts.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onAnswer(false);
      }}
    >
      <div
        className="confirm__panel"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
      >
        <h2 className="confirm__title" id="confirm-title">
          {title}
        </h2>
        {body && <div className="confirm__body">{body}</div>}
        <div className="confirm__actions">
          <div ref={cancelButton}>
            <Button variant="secondary" size="sm" onClick={() => onAnswer(false)}>
              {cancelLabel}
            </Button>
          </div>
          <Button
            variant={tone === 'danger' ? 'destructive' : 'primary'}
            size="sm"
            onClick={() => onAnswer(true)}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmProvider;
